import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { User, Mail, Phone, BookOpen, ShieldCheck, LogOut, Sparkles } from 'lucide-react';

export default function Profile() {
  const [user, setUser] = useState(null);
  const [purchasedCourses, setPurchasedCourses] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    if (!storedUser) {
      navigate('/login');
      return;
    }

    const userData = JSON.parse(storedUser);
    setUser(userData);

    // Fetch user purchased courses using relative path for Production & Local Dev
    if (userData.id) {
      fetch(`/api/user-purchases/${userData.id}`)
        .then((res) => res.json())
        .then((data) => {
          if (data.success) {
            setPurchasedCourses(data.purchasedCourseIds);
          }
        })
        .catch((err) => console.error("Profile Purchase Fetch Error:", err));
    }
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    navigate('/');
  };

  if (!user) return null;

  return (
    <div className="bg-[#FAF8F5] min-h-screen text-[#0B2540] font-sans py-12 px-6">
      <div className="max-w-4xl mx-auto space-y-8">
        
        {/* Profile Header */}
        <div className="bg-white rounded-3xl p-8 border border-gray-200/80 shadow-md flex flex-col sm:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-5">
            <div className="w-16 h-16 rounded-2xl bg-[#10B981] text-white font-extrabold text-2xl flex items-center justify-center shadow-lg shadow-[#10B981]/20 uppercase">
              {user.name ? user.name.charAt(0) : 'U'}
            </div>
            <div className="space-y-1">
              <h1 className="font-serif-title text-2xl font-bold">{user.name}</h1>
              <p className="text-xs text-gray-500 font-semibold">{user.email}</p>
              <span className="inline-flex items-center gap-1 bg-[#E6F4ED] text-[#10B981] text-[10px] font-bold px-2.5 py-0.5 rounded-full">
                <ShieldCheck className="w-3 h-3" /> Verified Member
              </span>
            </div>
          </div>

          <button
            onClick={handleLogout}
            className="flex items-center gap-2 bg-red-50 text-red-600 hover:bg-red-100 font-bold px-5 py-2.5 rounded-xl text-xs transition shrink-0"
          >
            <LogOut className="w-4 h-4" />
            Logout
          </button>
        </div>

        {/* Profile Info & Account Details Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          
          {/* Account Details Box */}
          <div className="bg-white rounded-3xl p-6 border border-gray-200/80 shadow-sm space-y-4">
            <h3 className="font-serif-title font-bold text-base border-b border-gray-100 pb-3">Account Details</h3>
            
            <div className="space-y-3">
              <div className="flex items-center gap-3 text-xs text-gray-600">
                <User className="w-4 h-4 text-[#10B981]" />
                <div>
                  <p className="text-[10px] text-gray-400 font-bold uppercase">Name</p>
                  <p className="font-semibold text-[#0B2540]">{user.name}</p>
                </div>
              </div>

              <div className="flex items-center gap-3 text-xs text-gray-600">
                <Mail className="w-4 h-4 text-[#10B981]" />
                <div>
                  <p className="text-[10px] text-gray-400 font-bold uppercase">Email</p>
                  <p className="font-semibold text-[#0B2540]">{user.email}</p>
                </div>
              </div>

              <div className="flex items-center gap-3 text-xs text-gray-600">
                <Phone className="w-4 h-4 text-[#10B981]" />
                <div>
                  <p className="text-[10px] text-gray-400 font-bold uppercase">Phone</p>
                  <p className="font-semibold text-[#0B2540]">{user.phone || 'N/A'}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Enrolled Courses Box */}
          <div className="md:col-span-2 bg-white rounded-3xl p-6 border border-gray-200/80 shadow-sm space-y-4">
            <div className="flex items-center justify-between border-b border-gray-100 pb-3">
              <h3 className="font-serif-title font-bold text-base flex items-center gap-2">
                <BookOpen className="w-5 h-5 text-[#10B981]" />
                Enrolled Courses & Active Subscriptions
              </h3>
              <span className="text-xs font-bold text-[#10B981] bg-[#E6F4ED] px-2.5 py-0.5 rounded-full">
                {purchasedCourses.length} Unlocked
              </span>
            </div>

            {purchasedCourses.length > 0 ? (
              <div className="space-y-3">
                {purchasedCourses.map((cId, idx) => (
                  <div key={idx} className="p-4 bg-[#FAF8F5] rounded-2xl border border-gray-200/70 flex items-center justify-between">
                    <div>
                      <p className="text-xs font-bold text-[#0B2540] uppercase tracking-wide">{cId.replace(/-/g, ' ')}</p>
                      <p className="text-[10px] text-[#10B981] font-semibold mt-0.5">Full Video Modules Unlocked</p>
                    </div>
                    <Link
                      to="/courses"
                      className="bg-[#10B981] hover:bg-[#0E9F6E] text-white font-bold text-xs px-4 py-2 rounded-xl transition shadow-sm"
                    >
                      Watch Lessons
                    </Link>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 space-y-3">
                <Sparkles className="w-8 h-8 text-gray-300 mx-auto" />
                <p className="text-xs text-gray-500">You haven't enrolled in any course yet.</p>
                <Link
                  to="/courses"
                  className="inline-block bg-[#0B2540] text-white text-xs font-bold px-5 py-2.5 rounded-xl hover:bg-[#10B981] transition"
                >
                  Explore 2026 Courses
                </Link>
              </div>
            )}
          </div>

        </div>

      </div>
    </div>
  );
}